import { useState } from 'react';
import { Loader2, Plus, Trash2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
	useAccountUserAliases,
	useAddAccountUserAlias,
	useRemoveAccountUserAlias
} from '@/hooks/use-account-users';
import type { AccountUserAlias } from '@/types';

const EMAIL_RE = /^.+@.+\..+$/;

const ERROR_COPY: Record<string, string> = {
	invalid_email: 'Invalid email format',
	alias_already_exists: 'This member already owns this alias',
	alias_taken_in_account: 'Another member in this account already owns this alias',
	forbidden: 'You do not have permission to manage these aliases',
	account_user_not_found: 'Member not found',
	alias_not_found: 'Alias no longer exists'
};

interface MemberAliasesSectionProps {
	accountUserId: number;
	canManage: boolean;
}

export function MemberAliasesSection({ accountUserId, canManage }: MemberAliasesSectionProps) {
	const { data: aliases = [], isLoading, isError } = useAccountUserAliases(accountUserId);
	const addMutation = useAddAccountUserAlias();
	const removeMutation = useRemoveAccountUserAlias();
	const [newEmail, setNewEmail] = useState('');
	const [confirmRemove, setConfirmRemove] = useState<AccountUserAlias | null>(null);

	const handleAdd = async () => {
		const trimmed = newEmail.trim().toLowerCase();
		if (!EMAIL_RE.test(trimmed)) {
			toast.error('Invalid email format');
			return;
		}
		try {
			await addMutation.mutateAsync({ accountUserId, email: trimmed });
			setNewEmail('');
			toast.success(`Added ${trimmed}`);
		} catch (err) {
			const reason = err instanceof Error ? err.message : 'add_failed';
			toast.error(ERROR_COPY[reason] ?? 'Failed to add alias');
		}
	};

	const handleRemove = async () => {
		if (!confirmRemove) return;
		try {
			await removeMutation.mutateAsync({ accountUserId, aliasId: confirmRemove.id });
			toast.success(`Removed ${confirmRemove.email}`);
			setConfirmRemove(null);
		} catch (err) {
			const reason = err instanceof Error ? err.message : 'remove_failed';
			toast.error(ERROR_COPY[reason] ?? 'Failed to remove alias');
		}
	};

	return (
		<div className='bg-muted/30 border-t px-4 py-3 space-y-3'>
			<div className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
				<Mail className='h-3 w-3' />
				Email aliases
				<span className='text-muted-foreground/70'>(used for inbound email routing)</span>
			</div>

			{canManage && (
				<div className='flex gap-2'>
					<Input
						type='email'
						value={newEmail}
						onChange={(e) => setNewEmail(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleAdd();
							}
						}}
						placeholder='alias@company.com'
						className='h-8 text-xs'
						disabled={addMutation.isPending}
					/>
					<Button
						size='sm'
						className='h-8'
						onClick={handleAdd}
						disabled={!newEmail.trim() || addMutation.isPending}
					>
						{addMutation.isPending ? (
							<Loader2 className='h-3 w-3 animate-spin' />
						) : (
							<>
								<Plus className='h-3 w-3 mr-1' />
								Add
							</>
						)}
					</Button>
				</div>
			)}

			{isError ? (
				<p className='text-xs text-destructive'>Failed to load aliases.</p>
			) : isLoading ? (
				<p className='text-xs text-muted-foreground'>Loading…</p>
			) : aliases.length === 0 ? (
				<p className='text-xs text-muted-foreground'>No aliases yet.</p>
			) : (
				<div className='space-y-1'>
					{aliases.map((alias) => (
						<div
							key={alias.id}
							className='flex items-center justify-between rounded-md bg-background px-2.5 py-1.5 border'
						>
							<span className='text-xs font-mono truncate'>{alias.email}</span>
							{canManage && (
								<Button
									variant='ghost'
									size='sm'
									className='h-6 w-6 p-0'
									onClick={() => setConfirmRemove(alias)}
									disabled={removeMutation.isPending}
								>
									<Trash2 className='h-3 w-3' />
								</Button>
							)}
						</div>
					))}
				</div>
			)}

			<AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove email alias?</AlertDialogTitle>
						<AlertDialogDescription>
							<span className='font-mono'>{confirmRemove?.email}</span> will no longer route inbound mail to this member. You can re-add it later.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={handleRemove} disabled={removeMutation.isPending}>
							{removeMutation.isPending ? 'Removing…' : 'Remove'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
